import * as React from "react";
import InputMask from "react-input-mask";

import { cn } from "@lab/src/lib/util";
import { Logger } from "@/lib/logger/Logger";

export interface InputProps
	extends React.InputHTMLAttributes<HTMLInputElement> {}

const TextDateInput = React.forwardRef<HTMLInputElement, InputProps>(
	({ className, ...props }, ref) => {
		return (
			// @ts-expect-error - I don't get it
			<InputMask
				mask="99.99.9999"
				placeholder="DD.MM.JJJJ"
				className={cn(
					"flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
					className
				)}
				inputRef={ref}
				{...props}
			/>
		);
	}
);
TextDateInput.displayName = "TextDateInput";

const TextDateInputDelayedSubmission: React.FC<
	InputProps & {
		onDateChange: (date: string | null) => void;
		value: string | null;
	}
> = ({ className, value, onDateChange, ...props }) => {
	const [internalValue, setInternalValue] = React.useState<string>(value);

	React.useEffect(() => {
		if (value !== internalValue) {
			const utcString = germanFormatToISO(value);
			setInternalValue(utcString);
		}
	}, [value]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		Logger.info(
			"handleDateChange",
			value,
			validateGermanDateFormat(value),
			germanFormatToISO(value)
		);
		const valueEmpty = !value || value?.length === 0;
		if (valueEmpty) {
			onDateChange?.(null);
		} else if (validateGermanDateFormat(value)) {
			onDateChange?.(germanFormatToISO(value));
		} else {
			setInternalValue(germanFormatToISO(value));
		}
	};

	return (
		<TextDateInput
			className={className}
			onChange={handleChange}
			value={isoToGermanFormat(internalValue) ?? ""}
			{...props}
		/>
	);
};

const validateGermanDateFormat = (value: string) => {
	const dateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
	return dateRegex.test(value);
};

const validateISOFormat = (value: string) => {
	const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
	return dateRegex.test(value);
};

/**
 * Converts a German date string (DD.MM.YYYY) to an ISO date string (YYYY-MM-DD).
 *
 * @param value - The date string in DD.MM.YYYY format.
 * @returns The date string in YYYY-MM-DD format, or null if the input is invalid.
 */
const germanFormatToISO = (value: string): string => {
	if (!validateGermanDateFormat(value)) {
		return value;
	}

	const parts = value.split(".");
	const day = parseInt(parts[0], 10);
	const month = parseInt(parts[1], 10);
	const year = parseInt(parts[2], 10);

	// Month is 0-indexed in JavaScript (0 = January).
	const date = new Date(year, month - 1, day);

	const yyyy = date.getFullYear();
	const mm = String(date.getMonth() + 1).padStart(2, "0");
	const dd = String(date.getDate()).padStart(2, "0");

	return `${yyyy}-${mm}-${dd}`;
};

const isoToGermanFormat = (value: string) => {
	if (!validateISOFormat(value)) {
		return value;
	}

	const date = new Date(value);
	return date.toLocaleDateString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
	});
};

export { TextDateInput, TextDateInputDelayedSubmission };
